import { useState, useEffect, useRef, useMemo } from "react";

const FieldDropdown = ({ value, onChange, compatibleFields, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredField, setHoveredField] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [referenceSearchTerm, setReferenceSearchTerm] = useState("");

  const dropdownId = useRef(Math.random().toString(36).substring(2, 11));
  const hideSubmenuTimer = useRef(null);

  const handleFieldHover = (fieldApiName, event) => {
    setHoveredField(fieldApiName);
    const rect = event.currentTarget.getBoundingClientRect();

    const submenuWidth = 280;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let left = rect.right - 4;
    let top = rect.top;

    if (left + submenuWidth > windowWidth) {
      left = rect.left - submenuWidth + 4;
    }

    const submenuMaxHeight = 300;
    if (top + submenuMaxHeight > windowHeight) {
      top = Math.max(10, windowHeight - submenuMaxHeight - 10);
    }

    setSubmenuPosition({ top, left });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('.field-dropdown-wrapper');
      const isClickInsideSubmenu = event.target.closest('.custom-dropdown-submenu');

      if (!isClickInsideDropdown && !isClickInsideSubmenu) {
        // Clear timer when clicking outside
        if (hideSubmenuTimer.current) {
          clearTimeout(hideSubmenuTimer.current);
          hideSubmenuTimer.current = null;
        }
        setIsOpen(false);
        setHoveredField(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleCloseOtherDropdowns = (event) => {
      if (event.detail !== dropdownId.current && isOpen) {
        setIsOpen(false);
        setHoveredField(null);
      }
    };

    document.addEventListener('dropdown-opened', handleCloseOtherDropdowns);

    return () => {
      document.removeEventListener('dropdown-opened', handleCloseOtherDropdowns);
    };
  }, [isOpen]);


  const parseValue = useMemo(() => {
    if (!value) return null;
    const parts = String(value).split(".");
    if (parts.length > 1) {
      return { objectLabel: parts[0].toLowerCase(), apiName: parts.slice(1).join(".") };
    }
    return { objectLabel: null, apiName: value };
  }, [value]);

  const formatGroupLabel = (label) => {
    if (!label) return "";
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const buildValue = (field) => {
    if (field.objectLabel) {
      return `${field.objectLabel}.${field.apiName}`;
    }
    return field.apiName;
  };

  const isSelectedField = (field) => {
    if (!parseValue || !field) return false;
    if (field.apiName !== parseValue.apiName) return false;
    if (parseValue.objectLabel && field.objectLabel) {
      return field.objectLabel === parseValue.objectLabel;
    }
    return true;
  };

  // Separate Deals properties (show directly) from other groups (show with submenu)
  const { dealsProperties, otherGroups } = (() => {
    const deals = [];
    const others = {};
    compatibleFields.forEach((field) => {
      const key = (field.objectLabel || "other").toLowerCase();
      if (key === "deals") {
        deals.push(field);
      } else {
        if (!others[key]) others[key] = [];
        others[key].push(field);
      }
    });
    return { dealsProperties: deals, otherGroups: others };
  })();

  // Sort groups: Contacts, Companies, Owners first, then others
  const sortedGroupKeys = (() => {
    const order = ["contacts", "companies", "owners"];
    const keys = Object.keys(otherGroups);
    const ordered = order.filter(key => keys.includes(key));
    const rest = keys.filter(key => !order.includes(key)).sort();
    return [...ordered, ...rest];
  })();

  const getDisplayText = () => {
    if (!value) return "-- None --";

    const field = compatibleFields.find(f => isSelectedField(f));
    if (field) {
      if (field.objectLabel === "deals") {
        return field.label;
      }
      return `${formatGroupLabel(field.objectLabel || "source")} ► ${field.label}`;
    }

    return value;
  };

  // Handle field selection
  const handleFieldSelect = (field) => {
    // Clear timer when selecting
    if (hideSubmenuTimer.current) {
      clearTimeout(hideSubmenuTimer.current);
      hideSubmenuTimer.current = null;
    }
    onChange(buildValue(field));
    setIsOpen(false);
    setHoveredField(null);
    setSearchTerm("");
    setReferenceSearchTerm("");
  };

  const handleToggleDropdown = () => {
    if (disabled) return; // Don't toggle if disabled

    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      const event = new CustomEvent('dropdown-opened', { detail: dropdownId.current });
      document.dispatchEvent(event);
    } else {
      // Clear timer when closing dropdown
      if (hideSubmenuTimer.current) {
        clearTimeout(hideSubmenuTimer.current);
        hideSubmenuTimer.current = null;
      }
      setHoveredField(null);
      setSearchTerm("");
      setReferenceSearchTerm("");
    }
  };


  return (
    <div className="field-dropdown-wrapper">
      <div
        className={`custom-dropdown-display ${disabled ? 'disabled' : ''}`}
        onClick={handleToggleDropdown}
        style={disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
      >
        <span>{getDisplayText()}</span>
        <svg
          className="dropdown-icon-inline"
          width="7.47"
          height="4.2"
          viewBox="0 0 8 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L4 4L7 1"
            stroke="#6A8991"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="custom-dropdown-menu" onClick={(e) => e.stopPropagation()}>
          <div className="custom-dropdown-search">
            <input
              type="text"
              placeholder="Search fields..."
              className="dropdown-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="custom-dropdown-options">
            {/* -- None -- option */}
            <div
              className={`custom-dropdown-option ${!value ? "selected" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleFieldSelect("");
              }}
            >
              <span>-- None --</span>
            </div>

            {/* Other groups (Contacts, Companies, Owners) with submenu */}
            {sortedGroupKeys.map((groupLabel) => {
              const groupFields = otherGroups[groupLabel] || [];
              const filteredGroupFields = groupFields.filter(field =>
                field.label.toLowerCase().includes(searchTerm.toLowerCase())
              );

              if (filteredGroupFields.length === 0) return null;

              return (
                <div
                  key={groupLabel}
                  className="custom-dropdown-option has-submenu"
                  onMouseEnter={(e) => {
                    // Clear any pending hide timer
                    if (hideSubmenuTimer.current) {
                      clearTimeout(hideSubmenuTimer.current);
                      hideSubmenuTimer.current = null;
                    }
                    
                    setHoveredField(groupLabel);
                    const rect = e.currentTarget.getBoundingClientRect();
                    const submenuWidth = 280;
                    const windowWidth = window.innerWidth;
                    const windowHeight = window.innerHeight;

                    // Position submenu to the right with some gap to avoid overlap
                    let left = rect.right + 8;
                    let top = rect.top;

                    // If submenu would go off screen, position it to the left instead
                    if (left + submenuWidth > windowWidth) {
                      left = rect.left - submenuWidth - 8;
                    }

                    const submenuMaxHeight = 300;
                    if (top + submenuMaxHeight > windowHeight) {
                      top = Math.max(10, windowHeight - submenuMaxHeight - 10);
                    }

                    setSubmenuPosition({ top, left });
                  }}
                  onMouseLeave={(e) => {
                    // Check if mouse is moving to another submenu item or the submenu itself
                    const relatedTarget = e.relatedTarget;
                    // Check if relatedTarget is a valid DOM element before calling closest
                    const isMovingToSubmenu = relatedTarget && typeof relatedTarget.closest === 'function' 
                      ? relatedTarget.closest('.custom-dropdown-submenu')
                      : null;
                    const isMovingToSubmenuItem = relatedTarget && typeof relatedTarget.closest === 'function'
                      ? relatedTarget.closest('.custom-dropdown-option.has-submenu')
                      : null;
                    
                    if (!isMovingToSubmenu && !isMovingToSubmenuItem) {
                      // Only hide if not moving to submenu or another submenu item
                      hideSubmenuTimer.current = setTimeout(() => {
                        const isStillHoveringSubmenu = document.querySelector('.custom-dropdown-submenu:hover');
                        const isStillHoveringSubmenuItem = document.querySelector('.custom-dropdown-option.has-submenu:hover');
                        
                        if (!isStillHoveringSubmenu && !isStillHoveringSubmenuItem) {
                          setHoveredField(null);
                        }
                      }, 200);
                    }
                  }}
                >
                  <span>{formatGroupLabel(groupLabel)}</span>
                  <svg
                    className="submenu-arrow"
                    width="5"
                    height="8"
                    viewBox="0 0 5 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L4 4L1 7"
                      stroke="#6A8991"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              );
            })}

            {/* Deals properties (show directly) */}
            {dealsProperties
              .filter(field =>
                field.label.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((field) => (
                <div
                  key={field.apiName}
                  className={`custom-dropdown-option ${isSelectedField(field) ? "selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFieldSelect(field);
                  }}
                >
                  <span>{field.label}</span>
                  <span className="field-type-label">({field.type.toLowerCase()})</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Render submenu outside of custom-dropdown-menu with fixed positioning */}
      {isOpen && hoveredField && sortedGroupKeys.includes(hoveredField) && (
        <div
          className="custom-dropdown-submenu"
          style={{
            top: `${submenuPosition.top}px`,
            left: `${submenuPosition.left}px`
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => {
            // Clear any pending hide timer when entering submenu
            if (hideSubmenuTimer.current) {
              clearTimeout(hideSubmenuTimer.current);
              hideSubmenuTimer.current = null;
            }
            setHoveredField(hoveredField);
          }}
          onMouseLeave={(e) => {
            // Check if mouse is moving to a submenu item
            const relatedTarget = e.relatedTarget;
            // Check if relatedTarget is a valid DOM element before calling closest
            const isMovingToSubmenuItem = relatedTarget && typeof relatedTarget.closest === 'function'
              ? relatedTarget.closest('.custom-dropdown-option.has-submenu')
              : null;
            
            if (!isMovingToSubmenuItem) {
              // Only hide if not moving to another submenu item
              hideSubmenuTimer.current = setTimeout(() => {
                const isStillHoveringSubmenuItem = document.querySelector('.custom-dropdown-option.has-submenu:hover');
                
                if (!isStillHoveringSubmenuItem) {
                  setHoveredField(null);
                }
              }, 200);
            }
          }}
        >
          <div className="custom-dropdown-search">
            <input
              type="text"
              placeholder={`Search ${formatGroupLabel(hoveredField)} fields...`}
              className="dropdown-search-input"
              value={referenceSearchTerm}
              onChange={(e) => setReferenceSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="custom-dropdown-options">
            {(otherGroups[hoveredField] || [])
              .filter(field =>
                !referenceSearchTerm || 
                field.label.toLowerCase().includes(referenceSearchTerm.toLowerCase()) ||
                field.apiName.toLowerCase().includes(referenceSearchTerm.toLowerCase()) ||
                field.type.toLowerCase().includes(referenceSearchTerm.toLowerCase())
              )
              .map((field) => (
                <div
                  key={`${hoveredField}-${field.apiName}`}
                  className={`custom-dropdown-option submenu-option ${isSelectedField(field) ? "selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFieldSelect(field);
                  }}
                >
                  <div className="submenu-option-content">
                    <span className="submenu-option-label">{field.label}</span>
                    <span className="submenu-option-type">{field.type.toLowerCase()}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldDropdown;
